class ScxSwiper extends HTMLElement {
    constructor() {
      super();
  
      this.swiperId = this.id;
      this.initialIndex = 0;
      this.dataSelector = this.getAttribute("data-selector");
      const loadingElem = this.getAttribute("data-loading-elem");
      this.dataTargetSelector = loadingElem;
      this.loadingDiv = this.querySelector(`.${loadingElem}`);
      this.swiperDiv = this.querySelector(`.${this.dataSelector}`);
      this.ignoreLazyLoading = this.shouldIgnoreLazyLoad();
      this.layoutConstraint = this.getAttribute("data-layout") === "true";
      
      // Cache for performance
      this.swiperInstance = null;
      this.isInitializing = false;
      this.isInitialized = false;
      this.observer = null;
      
      // Bind methods for better performance
      this.initSwiperInstance = this.initSwiperInstance.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }
  
    shouldIgnoreLazyLoad() {
      let dLazy = this.getAttribute("data-lazy");
      // Ignoring Lazy Load must be explicit
      if (!dLazy || dLazy === "") {
        return false;
      }
      return dLazy !== "true";
    }
  
    connectedCallback() {
      // Immediate initialization for critical swipers
      if (this.ignoreLazyLoading) {
        this.initializeImmediately();
      } else {
        this.initLazyLoading();
      }
    }
  
    disconnectedCallback() {
      this.cleanup();
    }
  
    initializeImmediately() {
      if (this.isInitialized || this.isInitializing) return;
      
      if (document.readyState === "complete" || document.readyState === "interactive") {
        this.initSwiperInstance();
      } else {
        // Use more efficient event listener
        const onReady = () => {
          this.initSwiperInstance();
          document.removeEventListener("DOMContentLoaded", onReady);
        };
        document.addEventListener("DOMContentLoaded", onReady, { once: true, passive: true });
      }
    }
  
    initLazyLoading() {
      if (this.isInitialized || this.isInitializing) return;
      
      // More efficient intersection observer
      const targetElement = document.querySelector(`.${this.dataTargetSelector}`);
      if (!targetElement) {
        console.warn("Target element not found for lazy loading:", this.dataTargetSelector);
        return;
      }
  
      this.observer = new IntersectionObserver(this.handleIntersection, {
        root: null,
        rootMargin: "50px 0px",
        threshold: 0
      });
  
      this.observer.observe(targetElement);
      
      // Fallback: Initialize after a reasonable delay if not intersected
      setTimeout(() => {
        if (!this.isInitialized && !this.isInitializing) {
          this.initSwiperInstance();
        }
      }, 3000);
    }
  
    handleIntersection(entries) {
      const entry = entries[0];
      if (entry.isIntersecting && !this.isInitialized && !this.isInitializing) {
        console.debug("Creating optimized swiper instance...");
        this.initSwiperInstance();
        
        // Stop observing immediately
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }
    }
  
    initSwiperInstance() {
      if (this.isInitialized || this.isInitializing) return;
      
      this.isInitializing = true;
      
      const selectorString = `.${this.dataSelector}`;
      const swiperElement = document.querySelector(selectorString);
      
      if (!swiperElement) {
        console.warn("Swiper element not found:", selectorString);
        this.isInitializing = false;
        return;
      }
  
      // Cleanup any existing instance
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }
  
      // Remove loading state immediately for better UX
      this.updateLoadingState();
  
      // Use requestAnimationFrame for smoother initialization
      requestAnimationFrame(() => {
        try {
          this.swiperInstance = new Swiper(selectorString, this.getSwiperOptions());
          this.isInitialized = true;
        } catch (error) {
          console.error("Failed to initialize Swiper:", error);
        } finally {
          this.isInitializing = false;
        }
      });
    }
  
    updateLoadingState() {
      if (this.loadingDiv) {
        this.loadingDiv.setAttribute("data-loaded", "true");
      }
      if (this.swiperDiv) {
        this.swiperDiv.setAttribute("data-loading", "false");
      }
    }
  
    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
      
      this.isInitialized = false;
      this.isInitializing = false;
    }
  
    openAtIndex(index) {
      this.initialIndex = index;
  
      if (this.swiperInstance && this.isInitialized) {
        this.swiperInstance.slideToLoop(index, 0);
      } else if (!this.isInitializing) {
        // Initialize and then go to index
        this.initSwiperInstance();
        setTimeout(() => {
          if (this.swiperInstance) {
            this.swiperInstance.slideToLoop(index, 0);
          }
        }, 100);
      }
    }
  
    getSwiperOptions = () => {
      const slidesPerView = this.getSlidesPerView();
      const spaceBetween = this.getSpaceBetween();
      const slidesBeforeAndAfter = this.getSlidesBeforeAndAfter();
  
      return {
        slidesPerView: slidesPerView.mobile,
        spaceBetween: spaceBetween.mobile,
        centeredSlides: this.getCenteredOption(),
        ...this.getMousewheelOptions(),
        ...this.getNavigationOptions(),
        ...this.getScrollbar(),
        ...this.getPaginationOptions(),
        ...this.getFreeModeOptions(),
        ...this.getLoopOptions(),
        ...this.getKeyboardOptions(),
        slidesOffsetAfter: slidesBeforeAndAfter.mobile.slidesOffsetAfter,
        slidesOffsetBefore: slidesBeforeAndAfter.mobile.slidesOffsetBefore,
        grabCursor: true,
        ...this.getZoomOption(),
        ...this.getLazy(),
        updateOnWindowResize: true,
        observer: false,
        observeParents: false,
        runCallbacksOnInit: false,
        breakpoints: {
          750: {
            slidesPerView: slidesPerView.tablet,
            spaceBetween: spaceBetween.tablet,
            slidesOffsetAfter: slidesBeforeAndAfter.tablet.slidesOffsetAfter,
            slidesOffsetBefore: slidesBeforeAndAfter.tablet.slidesOffsetBefore,
          },
          1200: {
            slidesPerView: slidesPerView.desktop,
            spaceBetween: spaceBetween.desktop,
            slidesOffsetAfter: slidesBeforeAndAfter.desktop.slidesOffsetAfter,
            slidesOffsetBefore: slidesBeforeAndAfter.desktop.slidesOffsetBefore,
          }
        },
        on: {
          init: (swiper) => {
            const initialSlide = parseInt(this.getAttribute("data-is"));
            if (!isNaN(initialSlide)) {
              swiper.slideTo(initialSlide, 0);
            }
            // Update loading state after successful initialization
            this.updateLoadingState();
          },
          beforeInit: () => {
            // Pre-initialize optimizations
            this.updateLoadingState();
          }
        },
      };
    };
  
    // Cache these method results for better performance
    getZoomOption = () => {
      if (!this._zoomOption) {
        const zoom = this.getAttribute("data-zoom");
        this._zoomOption = zoom === "true" ? { zoom: { panOnMouseMove: true } } : {};
      }
      return this._zoomOption;
    };
  
    getLazy = () => {
      if (!this._lazyOption) {
        const sLazy = this.getAttribute("data-swiper-lazy");
        this._lazyOption = sLazy === "true" ? { lazy: true } : {};
      }
      return this._lazyOption;
    };
  
    getCenteredOption = () => {
      if (this._centeredOption === undefined) {
        const centered = this.getAttribute("data-centered");
        this._centeredOption = centered === "true";
      }
      return this._centeredOption;
    };
  
    getScrollbar = () => {
      if (!this._scrollbarOption) {
        const elem = this.getAttribute("data-scrollbar-elem");
        this._scrollbarOption = elem ? {
          scrollbar: {
            el: elem,
            dragClass: "swiper-scrollbar-drag-s",
            hide: false,
            draggable: true,
          },
        } : {};
      }
      return this._scrollbarOption;
    };
  
    getSpaceBetween = () => {
      if (!this._spaceBetweenOption) {
        const mobileSpaceBetween = this.getAttribute("data-mobile-space-between") ?? "20px";
        const tabletSpaceBetween = this.getAttribute("data-tablet-space-between") ?? "20px";
        const desktopSpaceBetween = this.getAttribute("data-space-between") ?? "8px";
  
        this._spaceBetweenOption = {
          mobile: mobileSpaceBetween,
          tablet: tabletSpaceBetween,
          desktop: desktopSpaceBetween,
        };
      }
      return this._spaceBetweenOption;
    };
  
    getSlidesPerView = () => {
      if (!this._slidesPerViewOption) {
        const mobileSlidesPerView = this.getAttribute("data-mobile-slides-per-view") ?? 2.3;
        const tabletSlidesPerView = this.getAttribute("data-tablet-slides-per-view") ?? 3.7;
        const desktopSlidesPerView = this.getAttribute("data-slides-per-view") ?? 4.2;
  
        this._slidesPerViewOption = {
          mobile: mobileSlidesPerView,
          tablet: tabletSlidesPerView,
          desktop: desktopSlidesPerView,
        };
      }
      return this._slidesPerViewOption;
    };
  
    getNavigationOptions = () => {
      if (!this._navigationOption) {
        const nextElem = this.getAttribute("data-navigation-btn-next");
        const prevElem = this.getAttribute("data-navigation-btn-prev");
  
        this._navigationOption = (nextElem && prevElem) ? {
          navigation: {
            nextEl: nextElem,
            prevEl: prevElem,
          },
        } : {};
      }
      return this._navigationOption;
    };
  
    getPaginationOptions = () => {
      if (!this._paginationOption) {
        const mwElem = this.getAttribute("data-pagination-elem");
        this._paginationOption = mwElem ? {
          pagination: {
            el: mwElem,
            dynamicBullets: this.getAttribute("data-dynamic-bullets") === "true",
          },
        } : {};
      }
      return this._paginationOption;
    };
  
    getFreeModeOptions = () => {
      if (!this._freeModeOption) {
        const mwElem = this.getAttribute("data-free-mode");
        this._freeModeOption = mwElem === "true" ? { freeMode: true } : {};
      }
      return this._freeModeOption;
    };
  
    getLoopOptions = () => {
      if (!this._loopOption) {
        const mwElem = this.getAttribute("data-loop");
        this._loopOption = mwElem === "true" ? { loop: true } : {};
      }
      return this._loopOption;
    };
  
    getKeyboardOptions = () => {
      if (!this._keyboardOption) {
        const mwElem = this.getAttribute("data-keyboard-control");
        this._keyboardOption = mwElem === "true" ? { keyboard: { enabled: true } } : {};
      }
      return this._keyboardOption;
    };
  
    getMousewheelOptions = () => {
      if (!this._mousewheelOption) {
        const mwElem = this.getAttribute("data-mousewheel");
        this._mousewheelOption = mwElem === "true" ? { mousewheel: { forceToAxis: true } } : {};
      }
      return this._mousewheelOption;
    };
  
    getSlidesBeforeAndAfter() {
      if (!this._slidesBeforeAndAfterOption) {
        const breakpoints = ["mobile", "tablet", "desktop"];
        
        if (this.layoutConstraint) {
          this._slidesBeforeAndAfterOption = breakpoints.reduce((acc, bp) => {
            acc[bp] = {
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
            };
            return acc;
          }, {});
        } else {
          let offsetValue = this.getAttribute("data-offset");
          let defaultOffsetMob, defaultOffset;
  
          if (offsetValue) {
            defaultOffsetMob = defaultOffset = this.remToPx(parseFloat(offsetValue));
          } else {
            defaultOffsetMob = this.remToPx(1);
            defaultOffset = this.remToPx(1);
          }
  
          const slidesPerView = this.getSlidesPerView();
  
          this._slidesBeforeAndAfterOption = breakpoints.reduce((acc, bp) => {
            const offset = bp === "mobile" ? defaultOffsetMob : defaultOffset;
            acc[bp] = {
              slidesOffsetBefore: offset,
              slidesOffsetAfter: this.total === slidesPerView[bp] ? 0 : offset,
            };
            return acc;
          }, {});
        }
      }
      
      return this._slidesBeforeAndAfterOption;
    }
  
    remToPx(rem) {
      if (!this._rootFontSize) {
        this._rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      }
      return rem * this._rootFontSize;
    }
  }
  
  // Enhanced event handling for Shopify theme editor
  document.addEventListener("shopify:section:load", (event) => {
    const swipers = event.target.querySelectorAll('scx-swiper');
    swipers.forEach(swiper => {
      if (swiper.swiperInstance) {
        swiper.cleanup();
        swiper.connectedCallback();
      }
    });
  });
  
  document.addEventListener("shopify:section:unload", (event) => {
    const swipers = event.target.querySelectorAll('scx-swiper');
    swipers.forEach(swiper => {
      swiper.cleanup();
    });
  });
  
  customElements.define("scx-swiper", ScxSwiper);
  